/**
 * **Weapon-own damage shapes** (`ll1-features` 8.4) — two facets a weapon's own entry carries,
 * read here by the attack spine rather than off any fold (like `ignoreDifficulty` at the to-hit):
 * - `damageEqualsDistance` (the Fusion Rifle): the hit's damage is the distance to the target, up
 *   to a cap, in place of the frozen damage roll (Long Rim freezes it as `"1-6"`, which no dice
 *   expression parses).
 * - `reliableUnlessStructure` (the Unraveler): a hit that neither destroys its target nor deals it
 *   structure damage deals only its Reliable damage instead.
 * - `attacksTargets` / `lineToTarget` (the Sharanga Missiles, the Impact Lance): the attack fans
 *   out over more than one target ({@link multiTargetCatch}), through the area fan-out.
 * - `usableWhileJammed` (the Tiger-Hunter Combat Sheathe): read by the status-legality gate's caller.
 * - `sparesAllies` (the Veil Rifle): the area fan-out drops caught allies from the attack.
 * - `areaOnHit` (the Magnetic Cannon): once the attack hits anyone, the fan-out runs an effect on
 *   every character it attacked.
 */
import { worldFor, type Combatant, type EncounterState, type ResolvedStep, type WeaponProfile } from "@lancer-vtt/domain";
import type { RequestAttack } from "@lancer-vtt/shared";
import { footprintCells, footprintDistance } from "../effects/spatialAnswerer";
import { enumerateArea, type EffectAtom, type LoadedOverlay } from "../effects";
import { areaTemplate } from "./area";

/** The weapon entry's own modifier contributions, merged. */
function weaponOwn(overlay: LoadedOverlay | undefined, weaponId: string) {
  return (overlay?.[weaponId]?.effects ?? []).flatMap((e) => (e.kind === "modifier" ? [e.mod] : []));
}

/** `weapon` with its damage set to the distance to `target` when its entry carries `damageEqualsDistance`; else `weapon`. */
export function distanceScaledWeapon(args: { state: EncounterState; attacker: Combatant; target: Combatant; weapon: WeaponProfile; weaponId: string; overlay: LoadedOverlay | undefined }): WeaponProfile {
  const { state, attacker, target, weapon, weaponId, overlay } = args;
  const cap = weaponOwn(overlay, weaponId).find((m) => m.damageEqualsDistance !== undefined)?.damageEqualsDistance?.max;
  if (cap === undefined) return weapon;
  const world = worldFor(state);
  const dist = footprintDistance(world, footprintCells(world, attacker), footprintCells(world, target)) ?? 0;
  return { ...weapon, damage: { ...weapon.damage, count: 0, sides: 0, bonus: Math.min(cap, dist) } };
}

/** Whether the weapon's entry carries `reliableUnlessStructure`. */
export function reliableUnlessStructure(overlay: LoadedOverlay | undefined, weaponId: string): boolean {
  return weaponOwn(overlay, weaponId).some((m) => m.reliableUnlessStructure === true);
}

/** Whether a hit's steps destroy `targetId` or deal it structure damage. */
export function hitReachesStructure(steps: readonly ResolvedStep[], targetId: string): boolean {
  return steps.some((s) => (s.op === "structure_delta" && s.target === targetId && s.delta < 0) || (s.op === "destroy" && s.target === targetId));
}

/** The weapon entry's `attacksTargets` (how many it may attack at a time), or `undefined`. */
export function attacksTargets(overlay: LoadedOverlay | undefined, weaponId: string): number | undefined {
  return weaponOwn(overlay, weaponId).find((m) => m.attacksTargets !== undefined)?.attacksTargets;
}

/** The weapon entry's `sparesAllies` (the Veil Rifle), or `undefined`. */
export function sparesAllies(overlay: LoadedOverlay | undefined, weaponId: string): { effect: EffectAtom[] } | undefined {
  return weaponOwn(overlay, weaponId).find((m) => m.sparesAllies !== undefined)?.sparesAllies;
}

/** The weapon entry's `areaOnHit` (the Magnetic Cannon), or `undefined`. */
export function areaOnHit(overlay: LoadedOverlay | undefined, weaponId: string): { effect: EffectAtom[] } | undefined {
  return weaponOwn(overlay, weaponId).find((m) => m.areaOnHit !== undefined)?.areaOnHit;
}

/** The weapon entry's `lineToTarget`, or `undefined`. */
function lineToTarget(overlay: LoadedOverlay | undefined, weaponId: string): { heatPerExtra: number } | undefined {
  return weaponOwn(overlay, weaponId).find((m) => m.lineToTarget !== undefined)?.lineToTarget;
}

/** Whether the weapon's entry carries `usableWhileJammed`. */
export function usableWhileJammed(overlay: LoadedOverlay | undefined, weaponId: string): boolean {
  return weaponOwn(overlay, weaponId).some((m) => m.usableWhileJammed === true);
}

/** A multi-target weapon's settled targets, and the heat its extra targets cost the wielder. */
export type MultiTargetCatch = { targetIds: string[]; extraHeat: number };

/**
 * The targets a **multi-target** weapon attacks, when there is more than one:
 * - `attacksTargets` (the Sharanga Missiles): the unit `target` plus `intent.alsoTargets`, in that
 *   order, up to the cap (the referee has already range- and LoS-gated each).
 * - `lineToTarget` (the Impact Lance): every character in a Line from the wielder to the unit
 *   `target`, the target included, in id order; the wielder takes `heatPerExtra` per target past
 *   the first.
 * `undefined` for any other weapon, a cell target, or a single target, so the single-target spine
 * runs exactly as before.
 */
export function multiTargetCatch(args: { state: EncounterState; intent: RequestAttack; weaponId: string; overlay: LoadedOverlay | undefined }): MultiTargetCatch | undefined {
  const { state, intent, weaponId, overlay } = args;
  if (intent.target.kind !== "unit") return undefined;
  const primary = intent.target.id;
  const cap = attacksTargets(overlay, weaponId);
  if (cap !== undefined) {
    const ids = [...new Set([primary, ...(intent.alsoTargets ?? [])])].filter((id) => id !== intent.attacker).slice(0, cap);
    return ids.length > 1 ? { targetIds: ids, extraHeat: 0 } : undefined;
  }
  const line = lineToTarget(overlay, weaponId);
  const attacker = state.combatants[intent.attacker];
  const target = state.combatants[primary];
  if (line === undefined || attacker === undefined || target === undefined) return undefined;
  const world = worldFor(state);
  const dist = footprintDistance(world, footprintCells(world, attacker), footprintCells(world, target)) ?? 0;
  const template = areaTemplate(state, { ...intent, toward: undefined }, { kind: "line", size: dist });
  if (template === undefined) return undefined;
  const caught = enumerateArea({ state, selector: template.selector, actors: { self: attacker.id, attacker: attacker.id }, ...(template.toward !== undefined ? { toward: template.toward } : {}) });
  const ids = [...new Set([...caught.combatants.map((c) => c.id), primary])].filter((id) => id !== attacker.id).sort();
  return ids.length > 1 ? { targetIds: ids, extraHeat: line.heatPerExtra * (ids.length - 1) } : undefined;
}
